import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Send, Plus, Trash2, MessageSquare, Sparkles, Download, Scale, MessageCircle } from "lucide-react";
import "@/lib/i18n_p1";
import { toast } from "sonner";

// WhatsApp upgrade fallback while paid tiers are hidden.
const FIRM_PHONE_E164 = "9613936333"; // Eid Law Firm — keep in sync with LawFirm.jsx
const UPGRADE_PREFILLS = {
  en: (name) => `Hello, I'm ${name || "a Law Pilot user"} and I've reached my free consultations. I'd like to upgrade my plan — can you help?`,
  fr: (name) => `Bonjour, je suis ${name || "un utilisateur de Law Pilot"} et j'ai atteint la limite de mes consultations gratuites. J'aimerais passer à un plan supérieur — pouvez-vous m'aider ?`,
  ar: (name) => `مرحباً، أنا ${name || "أحد مستخدمي لو بايلوت"}. لقد بلغتُ حدّ الاستشارات المجانية وأودّ ترقية خطتي — هل يمكنكم المساعدة؟`,
};

export default function Chat() {
  const { conversation_id } = useParams();
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // { conversation_id, messages, title }
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const endRef = useRef(null);
  const initialSentRef = useRef(false);

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
  }, []);

  const loadActive = useCallback(async (id) => {
    if (!id) {
      setActive(null);
      return;
    }
    const { data } = await api.get(`/conversations/${id}`);
    setActive(data);
  }, []);

  useEffect(() => {
    const id = setTimeout(loadConvs, 0);
    return () => clearTimeout(id);
  }, [loadConvs]);

  useEffect(() => {
    const id = setTimeout(() => loadActive(conversation_id), 0);
    return () => clearTimeout(id);
  }, [conversation_id, loadActive]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length, streamBuffer]);

  const send = useCallback(async (textToSend) => {
    const msg = (textToSend ?? text).trim();
    if (!msg) return;
    let convId = conversation_id;
    if (!convId) {
      const { data } = await api.post("/conversations", { title: msg.slice(0, 60), language: lang });
      convId = data.conversation_id;
      await loadConvs();
      navigate(`/chat/${convId}`, { replace: true });
    }

    // Optimistic user message
    setActive((prev) => prev ? { ...prev, messages: [...(prev.messages || []), { role: "user", content: msg, message_id: `tmp_${Date.now()}` }] } : { conversation_id: convId, messages: [{ role: "user", content: msg, message_id: `tmp_${Date.now()}` }] });
    setText("");
    setStreaming(true); setStreamBuffer("");

    try {
      const resp = await fetch(`${API_BASE}/conversations/${convId}/messages/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, text: msg }),
      });
      if (!resp.ok || !resp.body) {
        let errTxt;
        try {
          const data = await resp.json();
          errTxt = typeof data?.detail === "string" ? data.detail : JSON.stringify(data);
        } catch { errTxt = await resp.text(); }
        if (resp.status === 402) {
          toast.error(errTxt || t(lang,"chat_ext.quota_warn"));
          throw new Error("quota_exceeded");
        }
        throw new Error(errTxt || "stream failed");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE: parse event blocks separated by \n\n
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const block of parts) {
          if (block.startsWith("event: done")) continue;
          if (block.startsWith("event: error")) {
            const m = block.match(/data:\s*(.*)/s);
            throw new Error(m ? m[1] : "stream error");
          }
          // collect all data: lines, joined with \n
          const lines = block.split("\n").filter((l) => l.startsWith("data:"));
          if (lines.length === 0) continue;
          const chunk = lines.map((l) => l.slice(5).replace(/^ /, "")).join("\n");
          acc += chunk;
          setStreamBuffer(acc);
        }
      }
      // Finalize: reload messages from server for canonical content
      await loadActive(convId);
      loadConvs();
    } catch (e) {
      console.error("stream error:", e);
      if (e?.message === "quota_exceeded") {
        // Show an inline upgrade prompt instead of a generic error.
        // Paid tiers are still coming — route users to WhatsApp for a manual upgrade.
        setActive((prev) => prev ? { ...prev, messages: [...(prev.messages || []), {
          role: "assistant",
          content: t(lang,"chat_ext.quota_warn"),
          message_id: `quota_${Date.now()}`,
          _quota: true,
        }] } : null);
      } else {
        // Try to recover: maybe the assistant message was saved on the server already.
        try { await loadActive(convId); } catch { /* ignore */ }
        const latest = await api.get(`/conversations/${convId}`).then(r => r.data).catch(() => null);
        const lastMsg = latest?.messages?.[latest.messages.length - 1];
        if (lastMsg && lastMsg.role === "assistant" && lastMsg.content) {
          // server already saved the answer — UI now reflects it; we're done
        } else {
          // Fall back to non-streaming endpoint so the user still gets an answer.
          try {
            const { data: msgData } = await api.post(`/conversations/${convId}/messages`, {
              conversation_id: convId, text: msg,
            });
            await loadActive(convId);
            if (!msgData) throw new Error("no message");
          } catch (e2) {
            console.error("fallback error:", e2);
            setActive((prev) => prev ? { ...prev, messages: [...(prev.messages || []), { role: "assistant", content: t(lang,"chat.error_fallback"), message_id: `err_${Date.now()}` }] } : null);
          }
        }
      }
    } finally {
      setStreaming(false); setStreamBuffer("");
    }
  }, [text, conversation_id, lang, navigate, loadActive, loadConvs]);

  // Send initial question from dashboard
  useEffect(() => {
    const q = location.state?.initialQuestion;
    if (q && conversation_id && !initialSentRef.current && active && (active.messages?.length || 0) === 0) {
      initialSentRef.current = true;
      send(q);
      // clear state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [conversation_id, active, location, navigate, send]);

  const onDelete = async (id) => {
    await api.delete(`/conversations/${id}`);
    if (id === conversation_id) navigate("/chat", { replace: true });
    loadConvs();
  };

  const messages = active?.messages || [];

  return (
    <AppShell hideFooter>
      <div className="grid grid-cols-12 gap-6 h-[calc(100dvh-180px)] min-h-[480px]">
        {/* Conversations list */}
        <aside className="hidden md:block col-span-4 lg:col-span-3 bg-white border border-[#E9ECEF] overflow-y-auto">
          <div className="p-4 border-b border-[#E9ECEF] flex items-center justify-between">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">{t(lang,"chat.conversations")}</h3>
            <button onClick={() => navigate("/chat")} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#0A1128] hover:bg-[#F8F9FA]" data-testid="chat-new-btn">
              <Plus className="w-3 h-3" /> {t(lang,"chat.new_chat")}
            </button>
          </div>
          <ul>
            {convs.length === 0 && (
              <li className="p-4 text-sm text-[#868E96]" data-testid="chat-conv-empty">{t(lang,"dash.no_chats")}</li>
            )}
            {convs.map((c) => (
              <li key={c.conversation_id} className={`group border-b border-[#E9ECEF] ${c.conversation_id === conversation_id ? "bg-[#F8F9FA] border-s-2 border-s-[#C65D47]" : ""}`}>
                <div className="flex items-center justify-between p-3">
                  <button onClick={() => navigate(`/chat/${c.conversation_id}`)} className="text-start flex-1" data-testid={`conv-item-${c.conversation_id}`}>
                    <div className="text-sm font-medium line-clamp-1">{c.title}</div>
                    <div className="text-xs text-[#868E96]">{new Date(c.updated_at).toLocaleDateString()}</div>
                  </button>
                  <button onClick={() => onDelete(c.conversation_id)} className="opacity-0 group-hover:opacity-100 text-[#868E96] hover:text-[#C92A2A] ms-2" data-testid={`conv-delete-${c.conversation_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main chat area */}
        <section className="col-span-12 md:col-span-8 lg:col-span-9 bg-white border border-[#E9ECEF] flex flex-col">
          <div className="border-b border-[#E9ECEF] px-6 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sparkles className="w-4 h-4 text-[#C65D47]" />
              <h2 className="font-display text-xl">{active?.title || t(lang,"chat.title")}</h2>
            </div>
            {active && messages.length > 0 && (
              <a
                href={`${API_BASE}/conversations/${conversation_id}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="chat-export-pdf-btn"
                className="inline-flex items-center gap-2 text-xs border border-[#0A1128] px-3 py-1.5 hover:bg-[#F8F9FA]"
              >
                <Download className="w-3.5 h-3.5" /> {t(lang,"chat_ext.export_pdf")}
              </a>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="chat-messages-container">
            {(!active || messages.length === 0) && !streaming ? (
              <div className="h-full flex flex-col items-center justify-center text-center max-w-xl mx-auto">
                <MessageSquare className="w-10 h-10 text-[#C65D47]" />
                <h3 className="mt-6 font-display text-3xl">{t(lang,"chat.empty_title")}</h3>
                <p className="mt-2 text-[#495057]">{t(lang,"chat.empty_sub")}</p>
                <div className="mt-8 grid grid-cols-1 gap-2 w-full">
                  {t(lang,"chat.suggestions").map((s, i) => (
                    <button key={i} onClick={() => { setText(s); send(s); }} className="text-start border border-[#E9ECEF] hover:border-[#C65D47] bg-[#F8F9FA] hover:bg-white p-4 text-sm transition-colors" data-testid={`suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((m) => (
                  <div key={m.message_id} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                    {m.role === "user" ? (
                      <div className="bg-[#F8F9FA] border border-[#E9ECEF] px-4 py-3 max-w-[85%] text-[#0A1128] whitespace-pre-wrap text-sm leading-relaxed" data-testid="chat-user-msg">
                        {m.content}
                      </div>
                    ) : (
                      <>
                        <div className="border-s-4 border-s-[#C65D47] ps-4 max-w-[95%] text-[#0A1128] whitespace-pre-wrap text-sm leading-relaxed" data-testid="chat-ai-msg">
                          {m.content}
                        </div>
                        {m._quota ? (
                          <a
                            href={`https://wa.me/${FIRM_PHONE_E164}?text=${encodeURIComponent((UPGRADE_PREFILLS[lang] || UPGRADE_PREFILLS.en)(user?.name || ""))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`chat-upgrade-whatsapp-${m.message_id}`}
                            className="mt-3 ms-1 inline-flex items-center gap-2 bg-[#25D366] text-white text-sm px-4 py-2 hover:bg-[#1DA851] transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            {t(lang,"chat_ext.upgrade_whatsapp")}
                          </a>
                        ) : (
                          <Link
                            to="/lawfirm"
                            data-testid={`chat-lawyer-cta-${m.message_id}`}
                            className="mt-2 ms-1 inline-flex items-center gap-2 text-xs text-[#495057] hover:text-[#C65D47] underline decoration-dotted underline-offset-4 transition-colors"
                          >
                            <Scale className="w-3.5 h-3.5" />
                            {t(lang,"chat_ext.talk_to_lawyer")}
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {streaming && (
                  <div className="flex justify-start">
                    <div className="border-s-4 border-s-[#C65D47] ps-4 max-w-[95%] whitespace-pre-wrap text-sm leading-relaxed text-[#0A1128]" data-testid="chat-streaming-msg">
                      {streamBuffer || <span className="text-[#868E96]">{t(lang,"chat.thinking")}</span>}
                      {streamBuffer && <span className="streaming-cursor" />}
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#E9ECEF] p-4 flex items-end gap-3">
            <textarea
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={t(lang,"chat.placeholder")}
              className="flex-1 resize-none border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128] text-sm max-h-40"
              disabled={streaming}
            />
            <button
              type="submit" disabled={streaming || !text.trim()}
              data-testid="chat-send-btn"
              className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-5 py-3 hover:bg-[#1C2538] disabled:opacity-60"
            >
              {t(lang,"chat.send")} <Send className="w-4 h-4" />
            </button>
          </form>
        </section>
      </div>
    </AppShell>
  );
}
